/**
 * Helpers for a DD/MM/YYYY masked text date field.
 *
 * Native `<input type="date">` renders in the browser's locale (so a US user
 * sees MM/DD/YYYY), which we can't control. These helpers back a plain text
 * input that always reads and writes DD/MM/YYYY, while the app keeps dates in
 * canonical ISO (`YYYY-MM-DD`) internally.
 */

/** Progressive DD/MM/YYYY mask for a text input: keeps only digits (max 8) and
 * inserts the slashes as the user types. */
export function formatDateInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** Parse a complete `DD/MM/YYYY` string into `YYYY-MM-DD`, validating that it
 * is a real calendar date (correct month/day ranges, leap years). Returns
 * `null` for incomplete or impossible dates. */
export function ddmmyyyyToISODate(value: string): string | null {
  const m = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12 || day < 1) return null;
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const daysInMonth = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (day > daysInMonth[month - 1]) return null;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

/** Render a `YYYY-MM-DD` (or a full ISO datetime — only the date part is used)
 * as `DD/MM/YYYY` for display. Returns `""` for empty/malformed input. */
export function isoDateToDDMMYYYY(value: string | null | undefined): string {
  if (!value) return '';
  const m = value.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
}
